import { decode } from "@msgpack/msgpack/dist";

/**
 * Creates websocket payload handler for series download transport protocols.
 * @param {Object} deps
 * @returns {(message: MessageEvent, error?: Error|string) => Promise<void>}
 */
const createOnReceiveSeriesDownloadDataHandler = ({
  router,
  dm,
  decrementInFlightDownloads,
  decrementInFlightDownloadsBy,
  processDownloadQueue,
  emitFatalSocketPayloadError,
  DOWNLOAD_TRANSPORT_MODE,
  pendingChunks,
  settleSeriesItemWithoutParse,
  settleBatchItemsWithoutParse,
  setSeriesItemInFlight,
  setSeriesItemParseQueued,
  seriesStateTrackers,
  maybeFinalizeSeries,
  toUint8Array,
  transitionSeriesItemState,
  ITEM_STATES,
  downloadPerf,
  store,
  downloadCounts,
  getInFlightDownloads,
  parseQueue,
  enqueueParseTask,
  drainParseQueue,
  getDeclaredSliceIndexForSeries,
  markSeriesItemReceived,
  enqueueParseBytesTask,
  registerCleanup
}) => {
  const CHUNK_PROGRESS_MIN_INTERVAL_MS = 250;
  const CHUNK_STALL_TIMEOUT_MS = 60000;
  const chunkProgressStateBySeries = new Map();
  const failedChunkPayloads = new Set();
  const chunkStallTimers = new Map();
  const safeCall = fn => {
    try {
      if (typeof fn === "function") {
        fn();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const commitPayloadAnnotations = (dbImageId, annotations) => {
    if (!annotations || annotations.length <= 0) {
      return;
    }
    store.commit("viewer/setAnnotations", {
      imageId: dbImageId,
      annotations
    });
  };

  const maybeCommitChunkProgress = ({ seriesId, received, totalSize }) => {
    if (!totalSize || totalSize <= 0) {
      return;
    }
    const counts = downloadCounts[seriesId];
    if (!counts) {
      return;
    }

    const alreadyLoaded = Number(counts.loaded) || 0;
    const totalImages = Number(counts.total) || 0;
    if (totalImages <= 0) {
      return;
    }

    const imageFraction = received / totalSize;
    const overallValue = Math.round(
      ((alreadyLoaded + imageFraction) / totalImages) * 100
    );
    const now = Date.now();
    const prev = chunkProgressStateBySeries.get(seriesId);
    if (
      prev &&
      prev.value === overallValue &&
      now - prev.ts < CHUNK_PROGRESS_MIN_INTERVAL_MS
    ) {
      return;
    }

    chunkProgressStateBySeries.set(seriesId, { value: overallValue, ts: now });
    store.commit("viewer/setDownloadProgress", {
      seriesId,
      value: overallValue,
      loaded: alreadyLoaded,
      total: totalImages,
      phase: "download"
    });
  };

  const clearChunkStallTimer = dbImageId => {
    const key = String(dbImageId);
    const timer = chunkStallTimers.get(key);
    if (!timer) {
      return;
    }
    clearTimeout(timer);
    chunkStallTimers.delete(key);
  };

  const clearAllChunkStallTimers = () => {
    chunkStallTimers.forEach(timer => clearTimeout(timer));
    chunkStallTimers.clear();
    failedChunkPayloads.clear();
  };

  if (typeof registerCleanup === "function") {
    registerCleanup(clearAllChunkStallTimers);
  }

  const armChunkStallTimer = ({ seriesId, dbImageId }) => {
    clearChunkStallTimer(dbImageId);
    // A chunked stream that stops before the sentinel otherwise leaves the
    // item in pendingChunks forever and blocks series finalization.
    const timer = setTimeout(() => {
      chunkStallTimers.delete(String(dbImageId));
      if (!pendingChunks[dbImageId]) {
        return;
      }
      failChunkPayload({
        seriesId,
        dbImageId,
        stage: "chunk_stall_timeout",
        message: `Failed to download series: chunked download stalled for image ${dbImageId}`
      });
    }, CHUNK_STALL_TIMEOUT_MS);
    chunkStallTimers.set(String(dbImageId), timer);
  };

  const failSeriesItem = ({
    seriesId,
    dbImageId,
    requestId = null,
    message,
    stage = "download_item_failed",
    rememberFailedChunk = false
  }) => {
    const key = String(dbImageId);
    if (rememberFailedChunk) {
      failedChunkPayloads.add(key);
    }
    clearChunkStallTimer(dbImageId);
    delete pendingChunks[dbImageId];
    transitionSeriesItemState(
      seriesStateTrackers[seriesId],
      dbImageId,
      ITEM_STATES.FAILED
    );
    safeCall(() =>
      downloadPerf.markItemState({
        seriesId,
        dbImageId,
        requestId,
        state: ITEM_STATES.FAILED
      })
    );
    safeCall(() =>
      downloadPerf.recordError({
        seriesId,
        dbImageId,
        requestId,
        stage,
        message
      })
    );
    setSeriesItemInFlight(seriesStateTrackers[seriesId], dbImageId, false);
    setSeriesItemParseQueued(seriesStateTrackers[seriesId], dbImageId, false);
    safeCall(() => dm.clearInFlight(seriesId, dbImageId));
    decrementInFlightDownloads();
    safeCall(() =>
      store.dispatch("raiseSnackbar", {
        text: message || "Failed to download series",
        color: "error",
        timeout: -1
      })
    );
    safeCall(() => maybeFinalizeSeries(seriesId, stage));
    safeCall(() => processDownloadQueue());
  };

  const failChunkPayload = payload => {
    failSeriesItem({
      stage: "chunk_payload_invalid",
      ...payload,
      rememberFailedChunk: true
    });
  };

  const handleInlinePayloadItem = ({
    item,
    chunkIndex = 0,
    onInvalidPayload
  } = {}) => {
    const {
      db_id,
      image_id,
      series_id,
      element_id,
      request_id,
      data: payloadData,
      annotations,
      total_size
    } = item || {};

    commitPayloadAnnotations(db_id, annotations);
    const fullArray = toUint8Array(payloadData);
    if (!fullArray) {
      if (typeof onInvalidPayload === "function") {
        onInvalidPayload({
          seriesId: series_id,
          dbImageId: db_id,
          requestId: request_id,
          chunkIndex
        });
      } else {
        failSeriesItem({
          seriesId: series_id,
          dbImageId: db_id,
          requestId: request_id,
          stage: "inline_payload_decode",
          message: `Failed to download series: invalid payload for image ${db_id}`
        });
      }
      return false;
    }

    markSeriesItemReceived({
      seriesId: series_id,
      dbImageId: db_id,
      requestId: request_id,
      payloadSize: total_size || fullArray.length,
      chunkIndex
    });
    enqueueParseBytesTask({
      seriesId: series_id,
      elementId: element_id,
      dbImageId: db_id,
      requestId: request_id,
      byteArray: fullArray,
      fileName: image_id
    });
    return true;
  };

  /**
   * Handles one websocket frame and routes it by transport payload type.
   * @param {MessageEvent} message
   * @param {Error|string} [error]
   * @returns {Promise<void>}
   */
  return async (message, error) => {
    // Parsing/rendering work is only allowed in viewer routes; elsewhere we only settle counters.
    const isViewerRoute =
      router.history.current.name === "viewer" ||
      router.history.current.name === "share";

    if (!(message.data instanceof Blob)) {
      if (isViewerRoute) {
        emitFatalSocketPayloadError({
          error,
          userMessage: error || "Failed to download series: no data received",
          perfStage: "ws_non_blob_payload"
        });
        return;
      }
      decrementInFlightDownloads();
      return;
    }

    let packedBuffer;
    try {
      packedBuffer = await message.data.arrayBuffer();
    } catch (arrayBufferError) {
      if (isViewerRoute) {
        console.error("Failed to read WS DICOM payload", arrayBufferError);
        emitFatalSocketPayloadError({
          error: arrayBufferError,
          userMessage: "Failed to read downloaded DICOM payload",
          perfStage: "ws_array_buffer"
        });
        return;
      }
      decrementInFlightDownloads();
      return;
    }

    let msg;
    try {
      msg = decode(packedBuffer);
    } catch (decodeError) {
      if (isViewerRoute) {
        console.error("Failed to decode WS DICOM payload", decodeError);
        emitFatalSocketPayloadError({
          error: decodeError,
          userMessage: "Failed to decode downloaded DICOM payload",
          perfStage: "ws_decode"
        });
        return;
      }
      decrementInFlightDownloads();
      return;
    }

    if (!isViewerRoute) {
      if (msg && msg.type === "error") {
        settleSeriesItemWithoutParse(
          msg.series_id,
          msg.db_id,
          msg.stage || "non_viewer_transport_error"
        );
        return;
      }

      if (msg && msg.type === "chunk") {
        if (msg.is_last) {
          delete pendingChunks[msg.db_id];
          settleSeriesItemWithoutParse(
            msg.series_id,
            msg.db_id,
            "non_viewer_chunk_last"
          );
        }
        return;
      }

      if (msg && msg.type === DOWNLOAD_TRANSPORT_MODE.SINGLE) {
        settleSeriesItemWithoutParse(
          msg.series_id,
          msg.db_id,
          "non_viewer_single_payload"
        );
        return;
      }

      if (msg && msg.type === DOWNLOAD_TRANSPORT_MODE.BATCH) {
        const items = Array.isArray(msg.items) ? msg.items : [];
        if (!items.length) {
          const expectedItems = Number.isFinite(msg.items_count)
            ? Math.max(1, Math.floor(msg.items_count))
            : 1;
          decrementInFlightDownloadsBy(expectedItems);
          return;
        }
        settleBatchItemsWithoutParse(items, "non_viewer_batch_payload");
        return;
      }

      decrementInFlightDownloads();
      return;
    }

    if (msg && msg.type === "error") {
      failChunkPayload({
        seriesId: msg.series_id,
        dbImageId: msg.db_id,
        requestId: msg.request_id,
        stage: msg.stage || "download_transport_error",
        message: "Failed to download series: transport error"
      });
      return;
    }

    // Chunk messages accumulate bytes progressively and enqueue parse only on the final chunk.
    if (msg && msg.type === "chunk") {
      const {
        db_id,
        image_id,
        series_id,
        element_id,
        chunk_index,
        is_last,
        data: chunkData,
        annotations,
        total_size,
        request_id
      } = msg;
      const chunkPayloadKey = String(db_id);

      if (failedChunkPayloads.has(chunkPayloadKey)) {
        if (is_last) {
          failedChunkPayloads.delete(chunkPayloadKey);
          clearChunkStallTimer(db_id);
        }
        return;
      }

      if (chunk_index === 0 && pendingChunks[db_id]) {
        failChunkPayload({
          seriesId: series_id,
          dbImageId: db_id,
          requestId: request_id,
          stage: "chunk_sequence_duplicate_start",
          message: `Failed to download series: duplicate first chunk for image ${db_id}`
        });
        return;
      }

      if (chunk_index === 0) {
        pendingChunks[db_id] = {
          chunks: [],
          received: 0,
          expectedNextChunkIndex: 0,
          totalSize: total_size ?? null,
          requestId: request_id ?? null,
          seriesId: series_id,
          elementId: element_id,
          imageId: image_id,
          annotations: annotations ?? []
        };
        armChunkStallTimer({ seriesId: series_id, dbImageId: db_id });
        downloadPerf.markFirstChunk({
          seriesId: series_id,
          dbImageId: db_id,
          requestId: request_id,
          totalSize: total_size
        });
        commitPayloadAnnotations(db_id, annotations);
      }

      const pending = pendingChunks[db_id];
      if (!pending) {
        if (is_last) {
          setSeriesItemInFlight(seriesStateTrackers[series_id], db_id, false);
          setSeriesItemParseQueued(seriesStateTrackers[series_id], db_id, false);
          dm.clearInFlight(series_id, db_id);
          decrementInFlightDownloads();
          maybeFinalizeSeries(series_id, "orphan_last_chunk");
          processDownloadQueue();
        }
        if (!is_last) {
          failChunkPayload({
            seriesId: series_id,
            dbImageId: db_id,
            requestId: request_id,
            stage: "chunk_missing_start",
            message: `Failed to download series: missing first chunk for image ${db_id}`
          });
        }
        return;
      }

      if (chunk_index !== pending.expectedNextChunkIndex) {
        failChunkPayload({
          seriesId: pending.seriesId,
          dbImageId: db_id,
          requestId: pending.requestId,
          stage: "chunk_sequence_mismatch",
          message: `Failed to download series: invalid chunk sequence for image ${db_id}`
        });
        return;
      }

      const chunkByteLength = chunkData
        ? chunkData.length ?? chunkData.byteLength ?? 0
        : 0;

      if (!is_last && chunkByteLength <= 0) {
        failChunkPayload({
          seriesId: pending.seriesId,
          dbImageId: db_id,
          requestId: pending.requestId,
          stage: "chunk_payload_empty",
          message: `Failed to download series: empty chunk payload for image ${db_id}`
        });
        return;
      }

      if (chunkByteLength > 0) {
        const chunk = toUint8Array(chunkData);
        if (!chunk) {
          failChunkPayload({
            seriesId: pending.seriesId,
            dbImageId: db_id,
            requestId: pending.requestId,
            stage: "chunk_payload_decode",
            message: `Failed to download series: invalid chunk payload for image ${db_id}`
          });
          return;
        }
        pending.chunks.push(chunk);
        pending.received += chunk.length;
        pending.expectedNextChunkIndex += 1;
        armChunkStallTimer({ seriesId: pending.seriesId, dbImageId: db_id });
        downloadPerf.markChunk({
          seriesId: pending.seriesId,
          dbImageId: db_id,
          requestId: pending.requestId,
          bytes: chunk.length,
          chunkIndex: chunk_index,
          inFlight: getInFlightDownloads(),
          parseQueueLength: parseQueue.length,
          pendingChunkCount: Object.keys(pendingChunks).length
        });
      }

      if (
        is_last &&
        pending.totalSize &&
        pending.totalSize > 0 &&
        pending.received !== pending.totalSize
      ) {
        failChunkPayload({
          seriesId: pending.seriesId,
          dbImageId: db_id,
          requestId: pending.requestId,
          stage: "chunk_size_mismatch",
          message: `Failed to download series: incomplete chunked payload for image ${db_id}`
        });
        return;
      }

      if (!is_last) {
        maybeCommitChunkProgress({
          seriesId: pending.seriesId,
          received: pending.received,
          totalSize: pending.totalSize
        });
        return;
      }

      delete pendingChunks[db_id];
      clearChunkStallTimer(db_id);
      transitionSeriesItemState(
        seriesStateTrackers[pending.seriesId],
        db_id,
        ITEM_STATES.RECEIVED
      );
      downloadPerf.markLastChunk({
        seriesId: pending.seriesId,
        dbImageId: db_id,
        requestId: pending.requestId,
        parseQueueLength: parseQueue.length
      });
      downloadPerf.markItemState({
        seriesId: pending.seriesId,
        dbImageId: db_id,
        requestId: pending.requestId,
        state: ITEM_STATES.RECEIVED
      });

      const totalLen = pending.chunks.reduce((s, c) => s + c.length, 0);
      const fullArray = new Uint8Array(totalLen);
      let offset = 0;
      for (const chunk of pending.chunks) {
        fullArray.set(chunk, offset);
        offset += chunk.length;
      }

      enqueueParseTask({
        seriesId: pending.seriesId,
        elementId: pending.elementId,
        dbImageId: db_id,
        requestId: pending.requestId,
        sliceIndex: getDeclaredSliceIndexForSeries(pending.seriesId, db_id),
        byteArray: fullArray,
        fileName: pending.imageId
      });

      drainParseQueue();
      return;
    }

    if (msg && msg.type === DOWNLOAD_TRANSPORT_MODE.SINGLE) {
      const enqueued = handleInlinePayloadItem({
        item: msg,
        chunkIndex: 0,
        onInvalidPayload: ({ seriesId, dbImageId, requestId }) => {
          failSeriesItem({
            seriesId,
            dbImageId,
            requestId,
            stage: "single_payload_decode",
            message: "Invalid single payload data"
          });
        }
      });
      if (!enqueued) {
        return;
      }
      drainParseQueue();
      return;
    }

    if (msg && msg.type === DOWNLOAD_TRANSPORT_MODE.BATCH) {
      const items = Array.isArray(msg.items) ? msg.items : [];
      if (!items.length) {
        const expectedItems = Number.isFinite(msg.items_count)
          ? Math.max(1, Math.floor(msg.items_count))
          : 1;
        decrementInFlightDownloadsBy(expectedItems);
        processDownloadQueue();
        downloadPerf.recordError({
          seriesId: msg?.series_id || null,
          stage: "batch_payload_empty",
          message: "Batch payload has no items"
        });
        store.dispatch("raiseSnackbar", {
          text: "Failed to download series: empty batch payload",
          color: "error",
          timeout: -1
        });
        return;
      }

      let enqueuedItems = 0;
      items.forEach((item, itemIndex) => {
        const enqueued = handleInlinePayloadItem({
          item,
          chunkIndex: itemIndex,
          onInvalidPayload: ({ seriesId, dbImageId, requestId }) => {
            failSeriesItem({
              seriesId,
              dbImageId,
              requestId,
              stage: "batch_item_payload_decode",
              message: `Invalid batch item payload at index ${itemIndex}`
            });
          }
        });
        if (enqueued) {
          enqueuedItems += 1;
        }
      });

      if (enqueuedItems > 0) {
        drainParseQueue();
      } else {
        processDownloadQueue();
      }
      return;
    }

    decrementInFlightDownloads();
    processDownloadQueue();
    console.error("Received unexpected WS message format", msg);
    downloadPerf.recordError({
      seriesId: msg?.series_id || null,
      dbImageId: msg?.db_id || null,
      requestId: msg?.request_id || null,
      stage: "unexpected_ws_payload",
      message: "Unexpected payload format"
    });
    store.dispatch("raiseSnackbar", {
      text: "Failed to download series: unexpected payload format",
      color: "error",
      timeout: -1
    });
  };
};

export { createOnReceiveSeriesDownloadDataHandler };
